"use client";

import { useState } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { submitAssistantResponseFeedback } from "@/lib/actions/assistant-feedback-actions";
import { captureClientEvent } from "@/lib/analytics/client";
import { buildAssistantFeedbackInput, type AssistantFeedbackRating, type AssistantResponseDeliveryMetadata } from "@/lib/ai/assistant-v2/response-delivery";
export function AssistantResponseFeedbackControls({
  responseId,
  deliveryMetadata,
  initialRating,
  onSaved
}: {
  responseId: string;
  deliveryMetadata: Readonly<AssistantResponseDeliveryMetadata>;
  initialRating?: AssistantFeedbackRating;
  onSaved?: (rating: AssistantFeedbackRating) => void;
}) {
  const [rating, setRating] = useState<AssistantFeedbackRating | undefined>(initialRating);
  const [pending, setPending] = useState<AssistantFeedbackRating | null>(null);
  const [error, setError] = useState(false);
  async function submit(nextRating: AssistantFeedbackRating) {
    if (pending) return;
    setPending(nextRating);
    setError(false);
    try {
      const result = await submitAssistantResponseFeedback(buildAssistantFeedbackInput({
        responseId,
        rating: nextRating
      }));
      if ("error" in result) {
        setError(true);
        return;
      }
      setRating(result.rating);
      onSaved?.(result.rating);
      captureClientEvent("assistant_response_feedback_submitted", {
        response_id: responseId,
        experience: deliveryMetadata.experience,
        response_kind: deliveryMetadata.responseKind,
        task: deliveryMetadata.task,
        rating: result.rating,
        graph_version: deliveryMetadata.graphVersion,
        router_model: deliveryMetadata.routerModel,
        response_model: deliveryMetadata.responseModel,
        verifier_model: deliveryMetadata.verifierModel,
        retrieval_index_version: deliveryMetadata.retrievalIndexVersion,
        trace_version: deliveryMetadata.traceVersion,
        evidence_count: deliveryMetadata.evidenceIds.length
      });
    } catch {
      setError(true);
    } finally {
      setPending(null);
    }
  }
  return <div className="assistant-response-feedback" aria-label="Rate this response">
            <button type="button" className="assistant-response-feedback-button" data-selected={rating === "helpful"} aria-label="Helpful response" aria-pressed={rating === "helpful"} disabled={pending !== null} onClick={() => void submit("helpful")}>
                <ThumbsUp aria-hidden="true" />
            </button>
            <button type="button" className="assistant-response-feedback-button" data-selected={rating === "not_helpful"} aria-label="Not helpful response" aria-pressed={rating === "not_helpful"} disabled={pending !== null} onClick={() => void submit("not_helpful")}>
                <ThumbsDown aria-hidden="true" />
            </button>
            {error && <span className="assistant-response-feedback-error" role="status">
                    Couldn&apos;t save rating
                </span>}
        </div>;
}